'use client';

import { Loader2 } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useSession } from '../lib/auth/client';

export default function StoresLayout({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const router = useRouter();
  const pathname = usePathname();

  // Public routes that don't require authentication
  const isPublicRoute = pathname?.includes('/application') || pathname?.includes('/signup');
  
  // Store browsing pages use their own layout with header
  const isStoreBrowsingPage = pathname?.match(/^\/stores\/[^/]+$/) || pathname?.match(/^\/stores\/[^/]+\/orders$/);

  useEffect(() => {
    // Skip auth checks for public routes
    if (isPublicRoute) return;

    if (!isPending && !session) {
      router.push('/auth/sign-in');
      return;
    }

    // Redirect to phone verification if not verified
    if (
      !isPending &&
      session &&
      'phoneNumberVerified' in session.user &&
      session.user.phoneNumberVerified === false
    ) {
      router.push('/auth/verify-phone');
    }
  }, [session, isPending, router, isPublicRoute]);

  // Show loading for authenticated routes only
  if (isPending && !isPublicRoute) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Render public routes without any layout wrapper
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Return null while redirecting for protected routes
  if (!session) {
    return null;
  }

  // Store browsing pages use their own full-screen layout
  if (isStoreBrowsingPage) {
    return <>{children}</>;
  }

  // Other store pages (dashboard, etc.) - could add sidebar back if needed
  return <>{children}</>;
}
